import { useEffect, useMemo, useRef, useState } from "react";
import { api, unwrapCollection } from "../utils/api";

const POLL_INTERVAL_MS = 15000;

function readStoredArray(key) {
  const stored = sessionStorage.getItem(key);
  if (!stored) return [];

  try {
    return JSON.parse(stored);
  } catch {
    return [];
  }
}

function writeStoredArray(key, value) {
  sessionStorage.setItem(key, JSON.stringify(value));
}

function getEarnedBadges(profile, posts, connections, userId, token) {
  if (!profile || !userId || !token) return [];

  const numericUserId = Number(userId);
  const userPosts = posts.filter(
    (post) => post.parentID === null && Number(post.authorID) === numericUserId
  );
  const userComments = posts.filter(
    (post) => post.parentID !== null && Number(post.authorID) === numericUserId
  );
  const activeFriends = connections.filter(
    (connection) => connection.attributes?.status === "active"
  );

  const staticBadges = [
    { name: "Account Created", isCompleted: true },
    { name: "First Post", isCompleted: userPosts.length > 0 },
    { name: "Add Bio", isCompleted: Boolean(profile.bio?.trim()) },
    {
      name: "5 Favorite Games",
      isCompleted: (profile.favoriteGames || []).length >= 5,
    },
  ];

  const engagementBadge = [
    { name: "Rookie Engagement", posts: 2, comments: 3 },
    { name: "Bronze Engagement", posts: 4, comments: 6 },
    { name: "Silver Engagement", posts: 8, comments: 12 },
    { name: "Gold Engagement", posts: 16, comments: 24 },
    { name: "Platinum Engagement", posts: 32, comments: 48 },
  ]
    .filter(
      (badge) =>
        userPosts.length >= badge.posts && userComments.length >= badge.comments
    )
    .slice(-1)[0];

  const friendBadge = [
    { name: "Friends Rookie", required: 1 },
    { name: "Friends Bronze", required: 5 },
    { name: "Friends Silver", required: 10 },
    { name: "Friends Gold", required: 20 },
    { name: "Friends Platinum", required: 40 },
  ]
    .filter((badge) => activeFriends.length >= badge.required)
    .slice(-1)[0];

  return [
    ...staticBadges.filter((badge) => badge.isCompleted),
    engagementBadge,
    friendBadge,
  ].filter(Boolean);
}

export function useNotifications(userId, token) {
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [connections, setConnections] = useState([]);
  const [badgeNotifications, setBadgeNotifications] = useState(() =>
    readStoredArray("badgeNotifications")
  );
  const seenBadgeIdsRef = useRef(new Set(readStoredArray("seenBadgeIds")));

  useEffect(() => {
    writeStoredArray("badgeNotifications", badgeNotifications);
  }, [badgeNotifications]);

  useEffect(() => {
    if (!userId || !token) return undefined;

    let cancelled = false;

    async function loadData() {
      if (document.visibilityState === "hidden") return;

      try {
        const [profileResult, postsResult, connectionsResult] = await Promise.all([
          api.get(`/users/${userId}`),
          api.get("/posts?limit=9999"),
          api.get(`/connections?anyUserID=${userId}`),
        ]);

        if (cancelled) return;

        setProfile(profileResult.attributes || profileResult);
        setPosts(unwrapCollection(postsResult));
        setConnections(unwrapCollection(connectionsResult));
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load notifications data:", error);
        }
      }
    }

    const handleVisible = () => {
      loadData();
    };

    loadData();
    const intervalId = window.setInterval(loadData, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", handleVisible);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisible);
    };
  }, [token, userId]);

  useEffect(() => {
    const earnedBadges = getEarnedBadges(profile, posts, connections, userId, token);
    if (!earnedBadges.length) return;

    const newNotifications = [];

    earnedBadges.forEach((badge) => {
      if (!seenBadgeIdsRef.current.has(badge.name)) {
        seenBadgeIdsRef.current.add(badge.name);
        newNotifications.push({
          id: badge.name,
          name: badge.name,
          message: `You earned the ${badge.name} badge!`,
          timestamp: new Date().toISOString(),
        });
      }
    });

    if (newNotifications.length) {
      writeStoredArray("seenBadgeIds", [...seenBadgeIdsRef.current]);
      setBadgeNotifications((current) => [...newNotifications, ...current]);
    }
  }, [connections, posts, profile, token, userId]);

  const pendingRequests = useMemo(
    () =>
      connections.filter(
        (connection) =>
          connection.attributes?.status === "pending" &&
          connection.toUser?.id === Number(userId)
      ),
    [connections, userId]
  );

  async function updateConnectionStatus(connectionId, status) {
    await api.patch(`/connections/${connectionId}`, {
      attributes: { status, type: "friend" },
    });

    const refreshedConnections = unwrapCollection(
      await api.get(`/connections?anyUserID=${userId}`)
    );
    setConnections(refreshedConnections);
  }

  function dismissBadge(index) {
    setBadgeNotifications((current) => current.filter((_, i) => i !== index));
  }

  return {
    badgeNotifications,
    dismissBadge,
    pendingRequests,
    updateConnectionStatus,
  };
}
